/**
 * Ground-truth puppet pose coordinates (world frame: Y-up, X right, Z forward).
 * Used as reference for display, RL reward, and comparison.
 * Matches rl/env.py DEFAULT_POSITIONS and STRING_NAMES order.
 */

export const ATTACHMENT_NAMES = [
  'head',
  'chest',
  'leftHand',
  'rightHand',
  'leftShoulder',
  'rightShoulder',
  'leftFoot',
  'rightFoot',
] as const

export type AttachmentName = (typeof ATTACHMENT_NAMES)[number]

export interface TruthPose {
  head: { x: number; y: number; z: number }
  chest: { x: number; y: number; z: number }
  leftHand: { x: number; y: number; z: number }
  rightHand: { x: number; y: number; z: number }
  leftShoulder: { x: number; y: number; z: number }
  rightShoulder: { x: number; y: number; z: number }
  leftFoot: { x: number; y: number; z: number }
  rightFoot: { x: number; y: number; z: number }
}

/** Base standing pose (matches RL env DEFAULT_POSITIONS). */
const STAND: TruthPose = {
  head: { x: 0.02, y: 1.46, z: -0.06 },
  chest: { x: 0.02, y: 1.11, z: -0.06 },
  leftHand: { x: -0.49, y: 1.01, z: -0.06 },
  rightHand: { x: 0.53, y: 1.01, z: -0.06 },
  leftShoulder: { x: -0.13, y: 1.01, z: -0.06 },
  rightShoulder: { x: 0.17, y: 1.01, z: -0.06 },
  leftFoot: { x: -0.08, y: 0.33, z: -0.01 },
  rightFoot: { x: 0.08, y: 0.33, z: -0.01 },
}

function applyOffsets(
  base: TruthPose,
  offsets: Partial<Record<AttachmentName, { x?: number; y?: number; z?: number }>>
): TruthPose {
  const out = { ...base }
  for (const key of ATTACHMENT_NAMES) {
    const o = offsets[key]
    if (!o) continue
    out[key] = {
      x: base[key].x + (o.x ?? 0),
      y: base[key].y + (o.y ?? 0),
      z: base[key].z + (o.z ?? 0),
    }
  }
  return out
}

/** Truth poses by name (keyframe targets for each motion). */
const TRUTH_POSES: Record<string, TruthPose> = {
  stand: STAND,
  idle: applyOffsets(STAND, {
    head: { y: 0.01 },
    chest: { y: 0.02 },
  }),
  hit: applyOffsets(STAND, {
    rightHand: { x: 0.08, z: 0.25 },
    rightShoulder: { z: 0.05 },
  }),
  wave: applyOffsets(STAND, {
    rightHand: { y: 0.12, z: 0.06 },
    rightShoulder: { z: 0.02 },
  }),
  point: applyOffsets(STAND, {
    rightHand: { y: -0.06, z: 0.18 },
    head: { y: -0.04, z: 0.02 },
  }),
  bow: applyOffsets(STAND, {
    head: { y: -0.08, z: 0.06 },
    chest: { y: -0.08, z: 0.06 },
    leftHand: { y: -0.06, z: 0.04 },
    rightHand: { y: -0.06, z: 0.04 },
    leftShoulder: { y: -0.06, z: 0.04 },
    rightShoulder: { y: -0.06, z: 0.04 },
  }),
  walk_left: applyOffsets(STAND, {
    leftFoot: { x: -0.12, z: -0.02 },
    rightFoot: { x: 0.04, z: 0.02 },
    chest: { x: -0.03 },
  }),
  walk_right: applyOffsets(STAND, {
    leftFoot: { x: -0.04, z: 0.02 },
    rightFoot: { x: 0.12, z: -0.02 },
    chest: { x: 0.03 },
  }),
  enter: applyOffsets(STAND, {
    head: { x: 0.15, y: 0.02 },
    chest: { x: 0.12 },
    leftHand: { x: 0.08 },
    rightHand: { x: 0.08 },
  }),
}

/**
 * Return truth pose for a named motion (stand, hit, wave, point, bow, walk_left, walk_right, idle, enter).
 * Returns null if name is unknown.
 */
export function getTruthPose(poseName: string): TruthPose | null {
  return TRUTH_POSES[poseName] ?? null
}

/** All pose names that have a truth definition. */
export function getTruthPoseNames(): string[] {
  return Object.keys(TRUTH_POSES)
}
